import { Box, CircularProgress, Stack } from "@mui/material";
import dayjs from "dayjs";
import advance from "dayjs/plugin/advancedFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";

import { useCurrentUser } from "api/services/useCurrentUser";
import { useLabelerStatus } from "api/services/useLabelerStatus";
import { ErrorType } from "components/common/ErrorView/types";
import { getErrorTypeFromStatus } from "components/common/ErrorView/utils";
import { UserStatus } from "config/constants";
import { DetectorProvider } from "context/detectorIdContext";
import { ServiceWorkerContext, ServiceWorkerContextType } from "context/serviceWorkerContext";
import { AppBar } from "features/appBar/AppBar";
import { useAppDispatch } from "hooks/store";
import { usePostEvent } from "hooks/usePostEvent";
import { useSentryUserContext } from "hooks/useSentryUserContext/useSentryUserContext";
import { CustomRoutes } from "routing/CustomRoutes";
import { RouteSpec } from "routing/routeSpecs";
import { setAuthUser } from "store/slices/Auth/authSlice";
import { User } from "types/users";

// Day.js configuration (moved outside component or to a one-time effect).
dayjs.extend(advance);
dayjs.extend(isSameOrBefore);
dayjs.extend(updateLocale);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

dayjs.updateLocale("en", {
  formats: {
    lll: "MMM D, YYYY h:mm:ss A",
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [routeSpec, setRouteSpecState] = useState<RouteSpec>();
  const [user, setUser] = useState<User | undefined>(undefined);
  const dispatch = useAppDispatch();

  useSentryUserContext();

  const memoizedSetRouteSpec = useCallback((spec: RouteSpec) => {
    setRouteSpecState(spec);
  }, []);

  const shouldShowErrorSnackbarCallback = useCallback(({ status = 200 }) => {
    const isAuthError = status === 401 || status === 403 || status === 419;
    if (isAuthError) {
      return false;
    }

    const errorType = getErrorTypeFromStatus(status);
    const hasErrorView = errorType !== null;
    return !hasErrorView;
  }, []);

  const { data, error, response } = useCurrentUser({
    shouldShowErrorSnackbar: shouldShowErrorSnackbarCallback,
  });

  const { updateLabelerStatus } = useLabelerStatus();

  const { postEvent: postCustomerLogin } = usePostEvent();

  // Service worker
  const { isUpdateReady } = useContext(ServiceWorkerContext) as ServiceWorkerContextType;

  useEffect(() => {
    const unloadListener = (event: BeforeUnloadEvent) => {
      // Cancels the event as stated by the standard.
      event.preventDefault();
      // Update the user status
      updateLabelerStatus({
        status: UserStatus.BROWSER_CLOSED,
      });
    };

    // when the app loads sets the status of the labeler to Active
    if (data?.only_labeler_ui && !isUpdateReady) {
      updateLabelerStatus({
        status: UserStatus.ACTIVE,
      });
      // Listener, before window is closed will update the status of the labeler to browser closed
      window.addEventListener("beforeunload", unloadListener);
    }

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", unloadListener);
    };
  }, [data?.only_labeler_ui, isUpdateReady, updateLabelerStatus]);

  useEffect(() => {
    if (user && data && !data?.only_labeler_ui) {
      postCustomerLogin("", {
        event_type: "user_login",
        user_name: data?.username,
      });
    }
  }, [data, postCustomerLogin, user]);

  // Set the user object based on the data returned from the /me API call
  useEffect(() => {
    if (data?.username) {
      setUser(data);
      dispatch(setAuthUser(data));
    }
  }, [data, dispatch, error]);

  // if the user isn't authorized (/me api call returns a 403) don't display content and instead
  // show simple error message
  const isUserDisabled = response?.status === 403;
  const errorTypeForView = isUserDisabled
    ? ErrorType.ERROR_USER_DISABLED
    : getErrorTypeFromStatus(response?.status || 200);

  return (
    <DetectorProvider>
      <Router>
        <Stack id="app" height="100%" width="100%">
          <ScrollToTop />
          <AppBar user={user} routeSpec={routeSpec} isUserDisabled={isUserDisabled} />
          {!isUserDisabled && !user ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
              <CircularProgress />
            </Box>
          ) : (
            <CustomRoutes user={user} setRouteSpec={memoizedSetRouteSpec} errorTypeForView={errorTypeForView} />
          )}
        </Stack>
      </Router>
    </DetectorProvider>
  );
}

export default App;
